//ReviewFrame.js
import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  RadioGroup,
  Radio,
  FormControl,
  Switch,
  Autocomplete,
  TextField,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import ActionListEditor from "./ActionListEditor";
import {
  Box,
  Alert,
  Typography,
  FormControlLabel,
  Paper,
  CircularProgress,
  Button,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchReviewRecords,
  setLabReport,
  clearLabReport,
  fetchLabReport,
  setLabReportActions,
  saveLabReportActions,
  approveLabReport,
  unapproveLabReport,
  toggleLiverun,
} from "../reducers/reviewFrameSlice";
import LabFilingDetails from "./LabFilingDetails";

import {
  SECTION_THEMES,
  sectionPaperSx,
  sectionHeadingSx,
} from "./sectionTheme";

const STATUS_OPTIONS = [
  "Not Approved",
  "Approved",
  "Approved and Closed",
  "Approved and In Process",
];

const STATUS_COLORS = {
  "Not Approved": "#f57c00",
  Approved: "#388e3c",
  "Approved and Closed": "#1565c0",
  "Approved and In Process": "#8e2456",
};

const STATUS_BUTTON_COLORS = {
  "Not Approved": { textHex: "#f57c00", bg: "#fff7e6" },
  Approved: { textHex: "#388e3c", bg: "#e8f5e9" },
  "Approved and Closed": { textHex: "#1565c0", bg: "#e3f2fd" },
  "Approved and In Process": { textHex: "#8e2456", bg: "#fce4ec" },
};

const EMPTY_FILTERS = {
  nhs_num: "",
  emis_id: "",
  lab_report_id: "",
  categories: [],
  ordering_parties: [],
  usual_gps: [],
  statuses: [],
  report_type: "",
  start_date: "",
  end_date: "",
};

const TABLE_COLUMNS = [
  {
    field: "nhs_num",
    headerName: "NHS Number",
    flex: 1,
    align: "center",
    headerAlign: "center",
    cellClassName: "bg-grey",
  },
  {
    field: "emis_id",
    headerName: "EMIS ID",
    flex: 1,
    align: "center",
    headerAlign: "center",
    cellClassName: "bg-grey",
  },
  {
    field: "lab_report_id",
    headerName: "Lab Report Id",
    flex: 1.6,
    align: "center",
    headerAlign: "center",
    cellClassName: "bg-grey",
  },
  {
    field: "category",
    headerName: "Category",
    flex: 2,
    align: "center",
    headerAlign: "center",
    cellClassName: "bg-grey",
  },
  {
    field: "is_normal",
    headerName: "Report Type (Is Normal)",
    flex: 1.2,
    align: "center",
    headerAlign: "center",
    cellClassName: "bg-grey",
    valueGetter: (value, row) => {
      const v = row ? row.is_normal : value;
      if (v === true) return "Normal";
      if (v === false) return "Abnormal";
      return "Unknown";
    },
  },
  {
    field: "ordering_party",
    headerName: "Ordering Party",
    flex: 1.5,
    align: "center",
    headerAlign: "center",
    cellClassName: "bg-grey",
  },
  {
    field: "usual_gp",
    headerName: "Usual GP",
    flex: 1.5,
    align: "center",
    headerAlign: "center",
    cellClassName: "bg-grey",
  },
  {
    field: "status",
    headerName: "Review And Approve Status",
    flex: 1.4,
    align: "center",
    headerAlign: "center",
    cellClassName: "bg-grey",
    renderCell: (params) => (
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          color: STATUS_COLORS[params.value] || "#4a5568",
        }}
      >
        {params.value}
      </Typography>
    ),
  },
  {
    field: "processed_time",
    headerName: "Processed Time",
    flex: 1.3,
    align: "center",
    headerAlign: "center",
    cellClassName: "bg-grey",
  },
  {
    field: "notes",
    headerName: "Notes",
    flex: 3,
    align: "center",
    headerAlign: "center",
    cellClassName: "bg-grey",
  },
];

const ReviewFrame = () => {
  const dispatch = useDispatch();
  const {
    liverun,
    records,
    totals,
    selectedLabReport,
    loadingRecords,
    lab,
    labReportActions,
    loadingLabReport,
    savingActions,
    updatingApproval,
  } = useSelector((state) => state.reviewFrame);

  const license_key = useSelector(
    (state) => state.authen.loggedInUser?.license_key,
  );

  const access_token = useSelector((state) => state.authen.accessToken);
  const is_admin = Boolean(access_token);

  const has_write = useSelector(
    (state) => state.authen.loggedInUser?.license_role === "Change",
  );

  const selectedRecord = useMemo(
    () => records.find((r) => r.lab_report_id === selectedLabReport),
    [records, selectedLabReport],
  );
  const currentStatus =
    selectedRecord?.status || (lab?.approved ? "Approved" : "Not Approved");
  const isClosed = currentStatus === "Approved and Closed";

  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [openDialog, setOpenDialog] = useState(false);

  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const [actionsValid, setActionsValid] = useState(true);
  const [showActionErrors, setShowActionErrors] = useState(false);

  const clearAlerts = () => {
    setMessage(null);
    setError(null);
  };

  // Load every record for the license
  useEffect(() => {
    if (license_key) {
      dispatch(fetchReviewRecords({ liverun, license_key }));
    }
  }, [liverun, license_key, dispatch]);

  const categoryOptions = useMemo(() => {
    const set = new Set();
    records.forEach((r) => {
      (r.categories || []).forEach((c) => set.add(c));
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [records]);

  const buildOptions = useCallback(
    (field) =>
      Array.from(
        new Set(records.map((r) => r[field]).filter((v) => v && v.trim())),
      ).sort((a, b) => a.localeCompare(b)),
    [records],
  );

  const orderingPartyOptions = useMemo(
    () => buildOptions("ordering_party"),
    [buildOptions],
  );
  const usualGpOptions = useMemo(
    () => buildOptions("usual_gp"),
    [buildOptions],
  );

  // Records arrive latest-first from the API; filtering keeps that order
  const filteredRecords = useMemo(() => {
    const {
      nhs_num,
      emis_id,
      lab_report_id,
      categories,
      ordering_parties,
      usual_gps,
      statuses,
      report_type,
      start_date,
      end_date,
    } = filters;

    return records.filter((row) => {
      if (
        nhs_num &&
        !String(row.nhs_num ?? "")
          .toLowerCase()
          .includes(nhs_num.toLowerCase())
      )
        return false;

      if (
        emis_id &&
        !String(row.emis_id ?? "")
          .toLowerCase()
          .includes(emis_id.toLowerCase())
      )
        return false;

      if (
        lab_report_id &&
        !String(row.lab_report_id ?? "")
          .toLowerCase()
          .includes(lab_report_id.toLowerCase())
      )
        return false;

      if (categories.length > 0) {
        if (!(row.categories || []).some((c) => categories.includes(c)))
          return false;
      }

      if (
        ordering_parties.length > 0 &&
        !ordering_parties.includes(row.ordering_party)
      )
        return false;

      if (usual_gps.length > 0 && !usual_gps.includes(row.usual_gp))
        return false;

      if (statuses.length > 0 && !statuses.includes(row.status)) return false;

      if (report_type !== "") {
        if (String(row.is_normal) !== report_type) return false;
      }

      // processed_time is "YYYY-MM-DD HH:MM:SS" so the first 10 chars is the date
      if (start_date || end_date) {
        const rowDate = String(row.processed_time || "").slice(0, 10);
        if (!rowDate) return false;
        if (start_date && rowDate < start_date) return false;
        if (end_date && rowDate > end_date) return false;
      }

      return true;
    });
  }, [records, filters]);

  const handleActionsChange = useCallback(
    (newActions) => {
      dispatch(setLabReportActions(newActions));
    },
    [dispatch],
  );

  const handleRowClick = (params) => {
    dispatch(setLabReport(params.row?.lab_report_id));
  };

  const handleOpenDialog = (params) => {
    const lab_report_id = params.row?.lab_report_id;
    clearAlerts();
    setShowActionErrors(false);
    dispatch(setLabReport(lab_report_id));
    dispatch(fetchLabReport({ lab_report_id, liverun, license_key }));
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    clearAlerts();
    setShowActionErrors(false);
    dispatch(clearLabReport());
  };

  const handleSave = () => {
    clearAlerts();

    if (!selectedLabReport) {
      setError("No lab report selected.");
      return;
    }

    if (!actionsValid) {
      setShowActionErrors(true);
      setError("Please complete all required action fields before saving.");
      return;
    }

    dispatch(
      saveLabReportActions({
        lab_report_id: selectedLabReport,
        actions: labReportActions,
        liverun,
        license_key,
      }),
    )
      .unwrap()
      .then(() => {
        setMessage("Actions saved successfully!");
      })
      .catch((error) => {
        setError(`Failed to save actions: ${error.message || error}`);
      });
  };

  const handleApprove = () => {
    clearAlerts();

    if (isClosed) {
      setError(
        "This report is Approved and Closed and can no longer be changed.",
      );
      return;
    }

    if (!selectedLabReport) {
      setError("No lab report selected.");
      return;
    }

    dispatch(
      approveLabReport({
        lab_report_id: selectedLabReport,
        liverun,
        license_key,
      }),
    )
      .unwrap()
      .then(() => {
        setMessage("Lab report approved successfully!");
      })
      .catch((error) => {
        setError(`Failed to approve: ${error.message || error}`);
      });
  };

  const handleUnapprove = () => {
    clearAlerts();

    if (isClosed) {
      setError(
        "This report is Approved and Closed and can no longer be changed.",
      );
      return;
    }

    if (!selectedLabReport) {
      setError("No lab report selected.");
      return;
    }

    dispatch(
      unapproveLabReport({
        lab_report_id: selectedLabReport,
        liverun,
        license_key,
      }),
    )
      .unwrap()
      .then(() => {
        setMessage("Lab report unapproved successfully!");
      })
      .catch((error) => {
        setError(`Failed to unapprove: ${error.message || error}`);
      });
  };

  const statusCounts = {
    "Not Approved": totals.not_approved,
    Approved: totals.approved,
    "Approved and Closed": totals.approved_closed,
    "Approved and In Process": totals.approved_inprocess,
  };

  const handleStatusToggle = (statusValue) => {
    setFilters((prev) => ({
      ...prev,
      statuses: prev.statuses.includes(statusValue)
        ? prev.statuses.filter((s) => s !== statusValue)
        : [...prev.statuses, statusValue],
    }));
  };

  return (
    <Box sx={{ height: "calc(100vh - 50px)", ml: -1, mr: -1 }}>
      {is_admin && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            mb: 0.5,
          }}
        >
          <FormControlLabel
            sx={{ m: 0 }}
            control={
              <Switch
                size="small"
                checked={liverun}
                onChange={() => dispatch(toggleLiverun())}
                color="success"
              />
            }
            label={
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, color: liverun ? "#2f855a" : "#c53030" }}
              >
                {liverun ? "Live Run" : "Dry Run"}
              </Typography>
            }
            labelPlacement="start"
          />
        </Box>
      )}

      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          mb: 1.5,
          borderRadius: 2,
          borderColor: "#cbd5e0",
          background: "#fafafa",
        }}
      >
        <Grid container spacing={1.5} alignItems="flex-start">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Search NHS Number"
              value={filters.nhs_num}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "");
                setFilters((prev) => ({ ...prev, nhs_num: value }));
              }}
              inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Search EMIS ID"
              value={filters.emis_id}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "");
                setFilters((prev) => ({ ...prev, emis_id: value }));
              }}
              inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Search Lab Report Id"
              value={filters.lab_report_id}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  lab_report_id: e.target.value,
                }))
              }
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Start Date"
                value={filters.start_date}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    start_date: e.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: filters.end_date || undefined }}
              />
              <TextField
                fullWidth
                size="small"
                type="date"
                label="End Date"
                value={filters.end_date}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    end_date: e.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: filters.start_date || undefined }}
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={8}>
            <Autocomplete
              multiple
              size="small"
              sx={{ minWidth: 500 }}
              options={categoryOptions}
              value={filters.categories}
              onChange={(event, newValue) => {
                setFilters((prev) => ({ ...prev, categories: newValue }));
              }}
              renderInput={(params) => (
                <TextField {...params} label="Search Category" />
              )}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Typography
              variant="caption"
              sx={{
                color: "#4a5568",
                fontWeight: 600,
                display: "block",
                mb: 0.5,
              }}
            >
              Filter by Report Type
            </Typography>

            <FormControl component="fieldset">
              <RadioGroup
                row
                value={filters.report_type}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    report_type: e.target.value,
                  }))
                }
              >
                <FormControlLabel
                  value=""
                  control={<Radio size="small" />}
                  label={<Typography variant="caption">All</Typography>}
                />
                <FormControlLabel
                  value="true"
                  control={<Radio size="small" />}
                  label={
                    <Typography variant="caption">Normal Report</Typography>
                  }
                />
                <FormControlLabel
                  value="false"
                  control={<Radio size="small" />}
                  label={
                    <Typography variant="caption">Abnormal Report</Typography>
                  }
                />
              </RadioGroup>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <Autocomplete
              multiple
              size="small"
              sx={{ pt: 2, minWidth: 200 }}
              options={orderingPartyOptions}
              value={filters.ordering_parties}
              onChange={(event, newValue) => {
                setFilters((prev) => ({ ...prev, ordering_parties: newValue }));
              }}
              renderInput={(params) => (
                <TextField {...params} label="Search Ordering Party" />
              )}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Autocomplete
              multiple
              size="small"
              sx={{ pt: 2, minWidth: 200 }}
              options={usualGpOptions}
              value={filters.usual_gps}
              onChange={(event, newValue) => {
                setFilters((prev) => ({ ...prev, usual_gps: newValue }));
              }}
              renderInput={(params) => (
                <TextField {...params} label="Search Usual GP" />
              )}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} md={8}>
            <Typography
              variant="caption"
              sx={{
                color: "#4a5568",
                fontWeight: 600,
                display: "block",
                mb: 0.5,
              }}
            >
              Filter by Review And Approve Status
            </Typography>

            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {STATUS_OPTIONS.map((status) => {
                const isActive = filters.statuses.includes(status);
                const colorInfo = STATUS_BUTTON_COLORS[status];
                const count = statusCounts[status];
                return (
                  <Button
                    key={status}
                    size="small"
                    variant={isActive ? "contained" : "outlined"}
                    onClick={() => handleStatusToggle(status)}
                    sx={{
                      textTransform: "none",
                      py: 0.25,
                      minHeight: 26,
                      ...(isActive
                        ? {
                            backgroundColor: colorInfo.textHex,
                            color: "#fff",
                            borderColor: colorInfo.textHex,
                            "&:hover": {
                              backgroundColor: colorInfo.textHex,
                              filter: "brightness(0.9)",
                            },
                          }
                        : {
                            color: colorInfo.textHex,
                            borderColor: colorInfo.textHex,
                            backgroundColor: colorInfo.bg,
                            "&:hover": {
                              backgroundColor: colorInfo.bg,
                              filter: "brightness(0.95)",
                              borderColor: colorInfo.textHex,
                            },
                          }),
                    }}
                  >
                    {status} (
                    {typeof count === "number"
                      ? count.toLocaleString()
                      : (count ?? 0)}
                    )
                  </Button>
                );
              })}
            </Box>
          </Grid>
          <Grid
            item
            xs={12}
            md={3}
            sx={{
              pt: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
            }}
          >
            <Button
              variant="outlined"
              onClick={() => setFilters(EMPTY_FILTERS)}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          height: "80%",
        }}
      >
        {loadingRecords ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataGrid
            rowHeight={25}
            rows={filteredRecords}
            columns={TABLE_COLUMNS}
            onRowClick={handleRowClick}
            onRowDoubleClick={handleOpenDialog}
            pageSizeOptions={[100]}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 100,
                },
              },
            }}
            getRowClassName={(params) => {
              if (params.row.lab_report_id === selectedLabReport) {
                return "selected-row";
              }
              return params.indexRelativeToCurrentPage % 2 === 0
                ? "even"
                : "odd";
            }}
            columnHeaderHeight={44}
            sx={{
              height: "100%",
              border: "none",
              width: "100%",
              "& .MuiDataGrid-columnHeaders": {
                minHeight: "44px !important",
                maxHeight: "44px !important",
                lineHeight: "44px !important",
                marginBottom: "16px",
              },
              "& .MuiDataGrid-columnHeader": {
                paddingTop: "4px",
                paddingBottom: "4px",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              },
              "& .MuiDataGrid-row": {
                cursor: "pointer",
              },
              "& .even": {
                backgroundColor: "#f9f9f9",
              },
              "& .odd": {
                backgroundColor: "#ffffff",
              },
              "& .selected-row": {
                backgroundColor: "#fef9c3 !important",
                outline: "2px solid #7c6fe0",
                outlineOffset: "-2px",
                fontWeight: 700,
              },
              "& .selected-row:hover": {
                backgroundColor: "#fef08a !important",
              },
              "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
                outline: "none",
              },
              "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
                {
                  outline: "none",
                },
            }}
          />
        )}
      </Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mt: 1, display: "block" }}
      >
        Showing {filteredRecords.length.toLocaleString()} of{" "}
        {records.length.toLocaleString()} reports. Double click a row to open
        the report.
      </Typography>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="xl"
        fullWidth
        scroll="paper"
      >
        <DialogTitle sx={{ fontWeight: "bold" }}>
          Advanced Filing Summary Report
          <Button
            onClick={handleCloseDialog}
            sx={{ float: "right" }}
            color="error"
            variant="outlined"
          >
            Close
          </Button>
        </DialogTitle>

        <DialogContent dividers>
          {loadingLabReport ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading lab report...</Typography>
            </Box>
          ) : lab && Object.keys(lab).length > 0 ? (
            <>
              <LabFilingDetails lab={lab} />
              <Paper elevation={3} sx={sectionPaperSx("actions")}>
                <Typography variant="h5" sx={sectionHeadingSx("actions")}>
                  6. Follow on actions:
                  <Box
                    component="span"
                    sx={{ color: SECTION_THEMES.actions.catColor }}
                  >
                    {Array.isArray(lab.categories) &&
                      lab.categories.length > 0 &&
                      lab.categories.map((cat, idx) => (
                        <React.Fragment key={cat.current_ruleset?.id || idx}>
                          {" "}
                          {cat.category_name}
                        </React.Fragment>
                      ))}
                  </Box>
                </Typography>

                <ActionListEditor
                  actions={labReportActions || []}
                  onChange={handleActionsChange}
                  readOnly={false}
                  onValidityChange={setActionsValid}
                  showAllErrors={showActionErrors}
                />

                <Box
                  sx={{
                    mt: 3,
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 2,
                    pointerEvents: has_write ? "auto" : "none",
                    opacity: has_write ? 1 : 0.6,
                  }}
                >
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={savingActions}
                    startIcon={
                      savingActions ? <CircularProgress size={16} /> : null
                    }
                  >
                    {savingActions ? "Saving..." : "Save Actions"}
                  </Button>
                </Box>
                {message && (
                  <Alert
                    severity="success"
                    onClose={() => setMessage(null)}
                    sx={{ mb: 2 }}
                  >
                    {message}
                  </Alert>
                )}
                {error && (
                  <Alert
                    severity="error"
                    onClose={() => setError(null)}
                    sx={{ mb: 2 }}
                  >
                    {error}
                  </Alert>
                )}
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  mb: 2,
                  pointerEvents: has_write ? "auto" : "none",
                  opacity: has_write ? (isClosed ? 0.5 : 1) : 0.2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: "bold",
                      color: lab.approved ? "#388e3c" : "#f57c00",
                    }}
                  >
                    Current Status: {lab.approved ? "Approved" : "Not Approved"}
                  </Typography>

                  {isClosed && (
                    <Typography variant="body2" color="text.secondary">
                      This report has been processed and closed, so its approval
                      can't be changed.
                    </Typography>
                  )}
                  <Box
                    sx={{
                      display: "flex",
                      gap: 3,
                      justifyContent: "center",
                      width: "100%",
                      maxWidth: 600,
                    }}
                  >
                    {!lab.approved ? (
                      <Button
                        variant="contained"
                        size="large"
                        color="success"
                        onClick={handleApprove}
                        disabled={updatingApproval || isClosed}
                        startIcon={
                          updatingApproval ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            <CheckCircleIcon />
                          )
                        }
                        sx={{
                          flex: 1,
                          py: 2,
                          fontSize: "2.1rem",
                          fontWeight: "bold",
                          textTransform: "none",
                        }}
                      >
                        {updatingApproval ? "Processing..." : "APPROVE"}
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        size="large"
                        color="warning"
                        onClick={handleUnapprove}
                        disabled={updatingApproval || isClosed}
                        startIcon={
                          updatingApproval ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            <CancelIcon />
                          )
                        }
                        sx={{
                          flex: 1,
                          py: 2,
                          fontSize: "2.1rem",
                          fontWeight: "bold",
                          textTransform: "none",
                        }}
                      >
                        {updatingApproval
                          ? "Processing..."
                          : "REVERT TO NOT APPROVED"}
                      </Button>
                    )}
                  </Box>
                </Box>
              </Paper>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ReviewFrame;
